import { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Listbox, Transition } from "@headlessui/react";
import { CountdownCircleTimer } from "react-countdown-circle-timer";

import { BsArrowRight, BsImage } from "react-icons/bs";
import { TiTick } from "react-icons/ti";

import {
	getFixedInputSwapQuote,
	getFixedOutputSwapQuote,
	getSwapQuoteTransactions,
	sendSwapQuoteTransactions,
} from "actions/transactions";
import { inputSwapQuoteLoadingStatus, sendingTransaction } from "actions/loaders";
import { getAccountInfoById, getAllAccountsByUser } from "actions/accounts";

import { findInObjectArrayByMatchingProperties } from "helpers/functions";
import useTheme from "hooks/useTheme";

import SendSwapTransactionModal from "theme/modals/swapAsset/SendSwapTransactionModal";
import SwapTransactionSuccessModal from "theme/modals/swapAsset/SwapTransactionSuccessModal";
import TransactionFailedModal from "theme/modals/TransactionFailedModal";
import SwapQuotationFailedModal from "theme/modals/swapAsset/SwapQuotationFailedModal";
import ChangeIcon from "theme/icons/Change";
import ExpandDownIcon from "theme/icons/ExpandDown";
import ArrowBackIcon from "theme/icons/ArrowBack";

const SwapAssets = ({ navigatePage }) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { appLanguage, themeColor, baseCurrency } = useTheme();
	const { asset } = location.state;

	const accounts = useSelector((state) => state.Accounts);
	const accountInfo = useSelector((state) => state.AccountInfo);

	const algoChain = useSelector((state) => state.AppPreferences.algoChain);
	const inputSwapQuoteLoading = useSelector((state) => state.InputSwapQuoteLoading);

	const [assets, setAssets] = useState(null);

	const [payingAsset, setPayingAsset] = useState(null);
	const [payingAmount, setPayingAmount] = useState(null);
	const [buyingAsset, setBuyingAsset] = useState(null);
	const [buyingAmount, setBuyingAmount] = useState(null);
	const [getQuotationType, setGetQuotationType] = useState(null);
	const [selectedAccount, setSelectedAccount] = useState(null);

	const [timerIsPlaying, setTimerIsPlaying] = useState(false);
	const [swapQuotePrice, setSwapQuotePrice] = useState(null);
	const [swapQuoteRoutes, setSwapQuoteRoutes] = useState(null);
	const [swapQuoteProfit, setSwapQuoteProfit] = useState(null);
	const [swapPriceImpact, setSwapPriceImpact] = useState(null);
	const [txnPayload, setTxnPayload] = useState(null);

	const [priceBaseline, setPriceBaseLine] = useState(null);
	const [slippage, setSlippage] = useState();
	const [minimumReceived, setMinimumReceived] = useState(null);
	const [payingAssetPrice, setPayingAssetPrice] = useState(null);
	const [buyingAssetPrice, setBuyingAssetPrice] = useState(null);

	const [minimumBalance, setMinimumBalance] = useState(null);
	const [insufficientAmount, setInsufficientAmount] = useState(false);

	const [txnGroup, setTxnGroup] = useState(false);
	const [sendSwapTransactionModalIsOpen, setSendSwapTransactionModalIsOpen] = useState(false);

	const [swapQuotationFailedModalIsOpen, setSwapQuotationFailedModalIsOpen] = useState(false);

	const [transactionSuccessModalIsOpen, setTransactionSuccessModalIsOpen] = useState(false);
	const [transactionSuccessModalInfo, setTransactionSuccessModalInfo] = useState(null);

	const [transactionFailedModalIsOpen, setTransactionFailedModalIsOpen] = useState(false);
	const [transactionFailedModalInfo, setTransactionFailedModalInfo] = useState(null);

	useEffect(() => {
		ClearAllStatesAndValues();
		if (!accounts) dispatch(getAllAccountsByUser());

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (accounts && !accountInfo)
			dispatch(getAccountInfoById(accounts[0]["account-address"], baseCurrency["currency-code"], algoChain));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accounts]);

	useEffect(() => {
		if (asset) setPayingAsset(asset);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [asset]);

	useEffect(() => {
		setAssets(null);

		if (accountInfo?.assets) {
			generateAssetListWithInfoByAccountAssets(accountInfo.assets);
		}

		if (accountInfo?.address) {
			const account = findInObjectArrayByMatchingProperties(accounts, { "account-address": accountInfo.address });

			setSelectedAccount(account[0]);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accountInfo]);

	useEffect(() => {
		if (selectedAccount?.["account-address"]) {
			setAssets(null);

			dispatch(getAccountInfoById(selectedAccount["account-address"], baseCurrency["currency-code"], algoChain));
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAccount]);

	useEffect(() => {
		if (assets && !payingAsset) {
			setPayingAsset(assets[0]);
			setGetQuotationType("input");
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assets]);

	useEffect(() => {
		if (slippage && swapQuotePrice && getQuotationType === "input") {
			setMinimumReceived(
				((swapQuotePrice * (100 - slippage)) / 100 / Math.pow(10, buyingAsset.decimals)).toLocaleString(undefined, {
					maximumFractionDigits: buyingAsset.decimals,
				})
			);

			setPayingAssetPrice(swapQuotePrice / (payingAmount * Math.pow(10, buyingAsset.decimals)));
			setBuyingAssetPrice(1 / (swapQuotePrice / (payingAmount * Math.pow(10, buyingAsset.decimals))));
		} else if (slippage && swapQuotePrice && getQuotationType === "output") {
			setMinimumReceived(
				((swapQuotePrice * (100 - slippage)) / 100 / Math.pow(10, payingAsset.decimals)).toLocaleString(undefined, {
					maximumFractionDigits: payingAsset.decimals,
				})
			);

			setPayingAssetPrice(swapQuotePrice / (buyingAmount * Math.pow(10, payingAsset.decimals)));
			setBuyingAssetPrice(1 / (swapQuotePrice / (buyingAmount * Math.pow(10, payingAsset.decimals))));
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slippage, swapQuotePrice]);

	useEffect(() => {
		if (priceBaseline && buyingAssetPrice && getQuotationType === "input")
			setSwapPriceImpact((buyingAssetPrice / priceBaseline - 1) * 100);
		else if (priceBaseline && payingAssetPrice && getQuotationType === "output")
			setSwapPriceImpact((payingAssetPrice / priceBaseline - 1) * 100);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payingAssetPrice, buyingAssetPrice, priceBaseline]);

	// set minimum balance for paying and buying asset
	useEffect(() => {
		if (payingAsset && getQuotationType === "input")
			setMinimumBalance(
				payingAsset["asset-id"] === -1
					? (payingAsset.amount - accountInfo["min-balance"] - 400000) / Math.pow(10, payingAsset.decimals)
					: payingAsset.amount / Math.pow(10, payingAsset.decimals)
			);
		else if (buyingAsset && getQuotationType === "output")
			setMinimumBalance(
				buyingAsset["asset-id"] === -1
					? (buyingAsset.amount - accountInfo["min-balance"] - 400000) / Math.pow(10, buyingAsset.decimals)
					: buyingAsset.amount / Math.pow(10, buyingAsset.decimals)
			);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payingAsset, buyingAsset, getQuotationType]);

	// check if entered amount can be sent or not
	// if amount is greater than the minimum sendable amount then show minimum amount on send button
	useEffect(() => {
		if (payingAsset && payingAmount && getQuotationType === "input")
			setInsufficientAmount(
				payingAsset?.["asset-id"] === -1
					? payingAmount > minimumBalance
						? true
						: false
					: payingAmount > minimumBalance
					? true
					: false
			);
		else if (buyingAsset && buyingAmount && getQuotationType === "output")
			setInsufficientAmount(
				buyingAsset?.["asset-id"] === -1
					? buyingAmount > minimumBalance
						? true
						: false
					: buyingAmount > minimumBalance
					? true
					: false
			);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [payingAmount, buyingAmount, getQuotationType]);

	// get all assets from account info and apply filter on assets list
	// remove NFT's and deleted assets
	const generateAssetListWithInfoByAccountAssets = async (assets) => {
		let assetsArray = [];

		const algorandAsset = {
			"asset-id": -1,
			name: "Algorand",
			"unit-name": "Algo",
			amount: accountInfo.amount,
			decimals: 6,
		};

		for (let asset of assets) {
			if (asset?.["is-nft"] === false && asset?.deleted === false) assetsArray.push(asset);
		}

		assetsArray = [...assetsArray].sort((a, b) => b.amount - a.amount);

		assetsArray.unshift(algorandAsset);

		setAssets(assetsArray);
	};

	// switch paying and buying asset with each on switch button
	const SwitchAssetsWithEachOther = () => {
		const tempPayingAsset = payingAsset;
		const tempPayingAmount = payingAmount;

		// setGetQuotationType("output");
		setSwapQuoteRoutes(null);

		setPayingAsset(buyingAsset);
		setBuyingAsset(tempPayingAsset);

		setPayingAmount(buyingAmount);
		setBuyingAmount(tempPayingAmount);
	};

	// Get swap quotation from alammex a defly service
	const GetQuote = async () => {
		dispatch(inputSwapQuoteLoadingStatus(true));
		setSwapQuoteRoutes(null);

		const formData = {
			algodUri: process.env.REACT_APP_ALGOD_MAINNET_BASE_URL,
			algodToken: {
				"x-api-key": process.env.REACT_APP_PURESTAKE_API_KEY,
			},
			algodPort: "",
			apiKey: process.env.REACT_APP_ALAMMEX_API_KEY,
			type: getQuotationType === "input" ? "fixed-input" : "fixed-output",
			amount: getQuotationType === "input" ? payingAmount : buyingAmount,
			fromASAID: payingAsset["asset-id"] === -1 ? 0 : payingAsset["asset-id"],
			toASAID: buyingAsset["asset-id"] === -1 ? 0 : buyingAsset["asset-id"],
			disabledProtocols: ["Tinyman", "FXDX"],
		};

		if (getQuotationType === "input") {
			await getFixedInputSwapQuote(formData, algoChain).then((data) => {
				if (data?.quote && data?.route) {
					setTxnPayload(data.txnPayload);
					setSwapQuotePrice(data.quote);
					setSwapQuoteRoutes(data.route);
					setSwapQuoteProfit(data.profit);
					setPriceBaseLine(data.priceBaseline);
					setBuyingAmount(data.quote / Math.pow(10, buyingAsset.decimals));
					setSlippage(0.5);
					setTimerIsPlaying(true);
				} else if (data?.error) {
					setSwapQuotationFailedModalIsOpen(true);
				}
			});
		} else if (getQuotationType === "output") {
			await getFixedOutputSwapQuote(formData, algoChain).then((data) => {
				if (data?.quote && data?.route) {
					setTxnPayload(data.txnPayload);
					setSwapQuotePrice(data.quote);
					setSwapQuoteProfit(data.profit);
					setSwapQuoteRoutes(data.route);
					setPriceBaseLine(data.priceBaseline);
					setPayingAmount(data.quote / Math.pow(10, payingAsset.decimals));
					setSlippage(0.5);
					setTimerIsPlaying(true);
				} else if (data?.error) {
					setSwapQuotationFailedModalIsOpen(true);
				}
			});
		}

		dispatch(inputSwapQuoteLoadingStatus(false));
	};

	// Get signed transactions data from alammex by payload
	const GetSwapTransactions = async () => {
		dispatch(inputSwapQuoteLoadingStatus(true));

		const formData = {
			algodUri: process.env.REACT_APP_ALGOD_MAINNET_BASE_URL,
			algodToken: {
				"x-api-key": process.env.REACT_APP_PURESTAKE_API_KEY,
			},
			algodPort: "",
			apiKey: process.env.REACT_APP_ALAMMEX_API_KEY,
			address: selectedAccount["account-address"],
			slippage: 0.5,
			txnPayloadJSON: txnPayload,
		};

		await getSwapQuoteTransactions(formData, algoChain).then((data) => {
			if (data?.txns) {
				setTxnGroup(data);
				setTimerIsPlaying(false);
				setSendSwapTransactionModalIsOpen(true);
			} else if (data?.error) {
				alert(JSON.stringify(data));
			}
		});

		dispatch(inputSwapQuoteLoadingStatus(false));
	};

	// Send signed transaction to blockchain
	const SendTransaction = async () => {
		dispatch(sendingTransaction(true));

		const formData = {
			"sender-account": selectedAccount["account-address"],
			txnGroup: txnGroup,
		};

		await sendSwapQuoteTransactions(formData, algoChain).then((data) => {
			if (data?.txID) {
				const modalInfo = { txID: data.txID };

				ClearAllStatesAndValues();
				setSendSwapTransactionModalIsOpen(false);
				setTransactionSuccessModalInfo(modalInfo);
				setTransactionSuccessModalIsOpen(true);
			} else if (data?.error) {
				const error = JSON.parse(JSON.stringify(data.error));

				const failedMessage = {
					title: data.message,
					"sub-title": "",
					message: <p className="break-words">{error.response.body.message}</p>,
				};

				ClearAllStatesAndValues();
				setSendSwapTransactionModalIsOpen(false);
				setTransactionFailedModalInfo(failedMessage);
				setTransactionFailedModalIsOpen(true);
			}
		});

		dispatch(sendingTransaction(false));
	};

	const SwapQuoteRoutesExpired = () => {
		setTimerIsPlaying(false);
		setSwapQuoteRoutes(null);

		if (getQuotationType === "input") setBuyingAmount(null);
		if (getQuotationType === "output") setPayingAmount(null);
	};

	const ClearAllStatesAndValues = () => {
		setPayingAmount(null);
		setBuyingAmount(null);
		setTimerIsPlaying(false);
		setSwapQuoteRoutes(null);
		setSwapQuotePrice(null);
		setSwapQuoteProfit(null);
		setSwapPriceImpact(null);
		setSlippage(null);
		setMinimumReceived(null);
		setBuyingAssetPrice(null);
		setPayingAssetPrice(null);
		setMinimumBalance(null);
		setInsufficientAmount(false);
		setTxnPayload(null);
		// setSelectedAccount(null);
		setPayingAsset(assets ? assets[0] : null);
		setBuyingAsset(null);
		setGetQuotationType("input");
		setTxnGroup(null);

		dispatch(inputSwapQuoteLoadingStatus(false));
	};

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
			leave="transition-opacity duration-250"
			leaveFrom="opacity-100"
			leaveTo="opacity-0"
		>
			<div className="flex flex-col w-full items-center justify-center">
				<div className="flex w-full items-center justify-start">
					<div className="w-14"></div>
					<ArrowBackIcon className={`w-4 cursor-pointer`} fill={themeColor?.hex} onClick={() => navigatePage(-1)} />
				</div>
				<div className="flex flex-col h-full w-[50%] items-start justify-start space-y-7">
					{/* Select Account Section */}
					<div className="relative flex flex-col w-full space-y-1">
						<Listbox
							value={selectedAccount}
							onChange={(e) => {
								setSelectedAccount(e);
								setPayingAsset(null);
								setBuyingAsset(null);
							}}
						>
							<div className="relative mt-1 w-full">
								<div className="flex space-x-2 items-center">
									{selectedAccount?.["profile-image"] ? (
										<img
											className={`w-7 object-cover object-center rounded-lg`}
											src={selectedAccount["profile-image"]}
											alt="temp"
										/>
									) : (
										<BsImage className={`text-4xl ${themeColor?.text}`} />
									)}
									<Listbox.Button
										className={`relative w-full cursor-default rounded-lg ${themeColor?.bg} bg-opacity-10 py-2 pl-3 pr-10 text-left shadow-md`}
									>
										<div className="flex space-x-2 items-center">
											<span className={`block truncate ${themeColor?.text}`}>
												{selectedAccount ? selectedAccount.name : appLanguage?.selectAccount}
											</span>
										</div>
										<span
											className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 ${themeColor?.text}`}
										>
											<ExpandDownIcon className="w-4" fill={themeColor?.hex} aria-hidden="true" />
										</span>
									</Listbox.Button>
								</div>
								<Transition
									as={Fragment}
									leave="transition ease-in duration-100"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
								>
									<Listbox.Options
										className={`z-10 z-10 absolute mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg `}
									>
										{accounts?.map((account, index) => (
											<Listbox.Option
												key={index}
												className={({ active }) =>
													`relative cursor-default select-none py-2 pl-10 pr-4 ${
														active ? "bg-amber-100 text-amber-900" : "text-gray-900"
													}`
												}
												value={account}
											>
												<>
													<div className="flex space-x-2 items-center">
														<span
															className={`block truncate ${themeColor?.text} ${
																selectedAccount?.["account-address"] ===
																account["account-address"]
																	? "font-bold"
																	: "font-normal"
															}`}
														>
															{account.name}
														</span>
													</div>

													{selectedAccount?.["account-address"] === account["account-address"] ? (
														<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
															<TiTick className="h-5 w-5" aria-hidden="true" />
														</span>
													) : null}
												</>
											</Listbox.Option>
										))}
									</Listbox.Options>
								</Transition>
							</div>
						</Listbox>
					</div>

					{/* Paying asset section */}
					<div className="flex flex-col w-full items-center justify-center space-y-3">
						<div className="relative flex flex-col w-full">
							<label className={`text-sm font-bold ${themeColor?.text}`}>{appLanguage?.pay}</label>

							<Listbox
								value={payingAsset}
								onChange={(e) => {
									setPayingAsset(e);
									setSwapQuoteRoutes(null);
								}}
							>
								<div className="relative mt-1 w-full">
									<div className="flex space-x-2 items-center">
										{payingAsset ? (
											<img
												className={`w-7 ${themeColor?.text}`}
												alt="asset"
												src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${payingAsset?.["asset-id"]}&width=30}`}
											/>
										) : (
											<div className="w-7"></div>
										)}
										<Listbox.Button
											className={`relative w-full cursor-default rounded-lg ${themeColor?.bg} bg-opacity-10 py-2 pl-3 pr-10 text-left shadow-md`}
										>
											<div className="flex space-x-2 items-center">
												<span className={`block truncate ${themeColor?.text}`}>
													{payingAsset
														? `${payingAsset.name} (${payingAsset["unit-name"]})`
														: "Select Asset"}
												</span>
											</div>
											<span
												className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 ${themeColor?.text}`}
											>
												<ExpandDownIcon className="w-4" fill={themeColor?.hex} aria-hidden="true" />
											</span>
										</Listbox.Button>
									</div>
									<Transition
										as={Fragment}
										leave="transition ease-in duration-100"
										leaveFrom="opacity-100"
										leaveTo="opacity-0"
									>
										<Listbox.Options
											className={`z-10 z-10 absolute mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg `}
										>
											{assets?.map((asset, index) => (
												<Listbox.Option
													key={index}
													className={({ active }) =>
														`relative cursor-default select-none py-2 pl-10 pr-4 ${
															active ? "bg-amber-100 text-amber-900" : "text-gray-900"
														}`
													}
													value={asset}
												>
													<>
														<div className="flex space-x-2 items-center">
															<img
																className={`w-7 ${themeColor?.text}`}
																alt=""
																src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${asset["asset-id"]}&width=30`}
															/>
															<span
																className={`block truncate ${themeColor?.text} ${
																	payingAsset?.["asset-id"] === asset["asset-id"]
																		? "font-bold"
																		: "font-normal"
																}`}
															>
																{asset.name} ({asset["unit-name"]})
															</span>
														</div>

														{payingAsset?.["asset-id"] === asset["asset-id"] ? (
															<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
																<TiTick className="h-5 w-5" aria-hidden="true" />
															</span>
														) : null}
													</>
												</Listbox.Option>
											))}
										</Listbox.Options>
									</Transition>
								</div>
							</Listbox>
						</div>

						<div className="flex w-full items-center justify-center space-x-2">
							<div className="w-8"></div>
							<div className="flex w-[50%] items-center justify-between">
								<div className="flex w-full items-center justify-start">
									<div className="flex flex-col w-fit items-start justify-center">
										<p className={`flex text-sm ${themeColor?.text}`}>{appLanguage?.balance}</p>
										<p className={`flex text-sm ${themeColor?.text}`}>
											{payingAsset &&
												(payingAsset.amount / Math.pow(10, payingAsset.decimals)).toLocaleString(
													undefined,
													{
														maximumFractionDigits: payingAsset.decimals,
													}
												)}
										</p>
									</div>
								</div>

								<p
									className={`text-sm font-medium ${themeColor?.text} cursor-pointer`}
									onClick={() => {
										setPayingAmount(minimumBalance);
										setGetQuotationType("input");
										setSwapQuoteRoutes(null);
									}}
								>
									{appLanguage?.max}
								</p>
							</div>
							<input
								className={`flex w-[50%] text-sm border rounded-lg px-3 py-3 shadow-md ${themeColor?.bg} bg-opacity-10 focus:outline-none ${themeColor?.text}`}
								placeholder="0.0"
								type="number"
								min="0"
								value={payingAmount ? payingAmount : ""}
								onChange={(e) => {
									setPayingAmount(e.target.value);
									setGetQuotationType("input");
									setSwapQuoteRoutes(null);
								}}
							/>
						</div>
					</div>

					{/* Buying asset section */}
					<div className="relative flex flex-col w-full space-y-3">
						<div className="flex w-full items-center justify-between">
							<label className={`text-sm font-bold ${themeColor?.text}`}>{appLanguage?.buy}</label>
							<ChangeIcon
								className={`w-5 cursor-pointer`}
								fill={themeColor?.hex}
								onClick={() => SwitchAssetsWithEachOther()}
							/>
						</div>
						<Listbox
							value={buyingAsset}
							onChange={(e) => {
								setBuyingAsset(e);
								setSwapQuoteRoutes(null);
							}}
						>
							<div className="relative mt-1 w-full">
								<div className="flex space-x-2 items-center">
									{buyingAsset ? (
										<img
											className={`w-7 ${themeColor?.text}`}
											alt="asset"
											src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${buyingAsset?.["asset-id"]}&width=30`}
										/>
									) : (
										<div className="w-7"></div>
									)}
									<Listbox.Button
										className={`relative w-full cursor-default rounded-lg ${themeColor?.bg} bg-opacity-10 py-2 pl-3 pr-10 text-left shadow-md`}
									>
										<div className="flex space-x-2 items-center">
											<span className={`block truncate ${themeColor?.text}`}>
												{buyingAsset
													? `${buyingAsset.name} (${buyingAsset["unit-name"]})`
													: "Select Asset"}
											</span>
										</div>
										<span
											className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 ${themeColor?.text}`}
										>
											<ExpandDownIcon className="w-4" fill={themeColor?.hex} aria-hidden="true" />
										</span>
									</Listbox.Button>
								</div>
								<Transition
									as={Fragment}
									leave="transition ease-in duration-100"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
								>
									<Listbox.Options className="z-10 absolute mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg">
										{assets?.map((asset, index) => (
											<Listbox.Option
												key={index}
												className={({ active }) =>
													`relative cursor-default select-none py-2 pl-10 pr-4 ${
														active ? "bg-amber-100 text-amber-900" : "text-gray-900"
													}`
												}
												value={asset}
											>
												<>
													<div className="flex space-x-2 items-center">
														<img
															className={`w-7 ${themeColor?.text}`}
															alt=""
															src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${asset["asset-id"]}&width=30`}
														/>
														<span
															className={`block truncate ${themeColor?.text} ${
																buyingAsset?.["asset-id"] === asset["asset-id"]
																	? "font-bold"
																	: "font-normal"
															}`}
														>
															{asset.name} ({asset["unit-name"]})
														</span>
													</div>

													{buyingAsset?.["asset-id"] === asset["asset-id"] ? (
														<span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
															<TiTick className="h-5 w-5" aria-hidden="true" />
														</span>
													) : null}
												</>
											</Listbox.Option>
										))}
									</Listbox.Options>
								</Transition>
							</div>
						</Listbox>

						<div className="flex w-full items-center justify-center space-x-2">
							<div className="w-8"></div>
							<div className="flex w-[50%] items-center justify-between">
								<div className="flex w-full items-center justify-start">
									<div className="flex flex-col w-fit items-start justify-center">
										<p className={`flex text-sm ${themeColor?.text}`}>{appLanguage?.balance}</p>
										<p className={`flex text-sm ${themeColor?.text}`}>
											{buyingAsset &&
												(buyingAsset.amount / Math.pow(10, buyingAsset.decimals)).toLocaleString(
													undefined,
													{
														maximumFractionDigits: buyingAsset.decimals,
													}
												)}
										</p>
									</div>
								</div>
							</div>
							<input
								className={`flex w-[50%] text-sm border rounded-lg px-3 py-3 shadow-md ${themeColor?.bg} bg-opacity-10 focus:outline-none ${themeColor?.text}`}
								placeholder="0.0"
								type="number"
								min="0"
								value={buyingAmount ? buyingAmount : ""}
								onChange={(e) => {
									setBuyingAmount(e.target.value);
									setGetQuotationType("output");
									setSwapQuoteRoutes(null);
								}}
							/>
						</div>
					</div>

					{/* Get quote section */}
					{!swapQuoteRoutes &&
						(inputSwapQuoteLoading ? (
							<img className="animate-spin mx-auto h-5 w-auto" src="/media/logo.png" alt="img" />
						) : (
							<div className="flex w-full items-center justify-center space-x-2">
								<button
									className={`w-full text-md font-bold px-7 py-3 rounded-2xl text-white ${themeColor?.bg} disabled:${themeColor?.bg} disabled:bg-opacity-40 disabled:text-white`}
									onClick={() => GetQuote()}
									disabled={
										(payingAmount || buyingAmount) && selectedAccount && payingAsset && buyingAsset
											? false
											: true
									}
								>
									{appLanguage?.convert}
								</button>
								<button
									className={`w-fit text-md font-bold px-4 py-2 rounded-2xl text-white ${themeColor?.bg}`}
									onClick={() => ClearAllStatesAndValues()}
								>
									{appLanguage?.clear}
								</button>
							</div>
						))}

					{/* Swap quotes route data */}
					{swapQuoteRoutes && (
						<div className="flex-col w-full items-start justify-start space-y-1">
							<div className="flex w-full space-x-2 items-center justify-start -z-50">
								<CountdownCircleTimer
									isPlaying={timerIsPlaying}
									size={40}
									strokeWidth={5}
									duration={30}
									isSmoothColorTransition={true}
									rotation={-1}
									colors={[themeColor?.hex, "#FC4242"]}
									colorsTime={[10, 0]}
									onComplete={() => SwapQuoteRoutesExpired()}
								>
									{({ remainingTime }) => <p className={`text-xs ${themeColor?.text}`}>{remainingTime}s</p>}
								</CountdownCircleTimer>
								<p className={`text-lg ${themeColor?.text}`}>{appLanguage?.routes}</p>
							</div>

							{swapQuoteRoutes.map((swapQuoteRoute, index) => (
								<div className="flex w-full space-x-2 items-start justify-start" key={index}>
									<p className={`text-sm ${themeColor?.text}`}>{swapQuoteRoute.percentage}%</p>

									<div>
										{swapQuoteRoute.path?.map((path, index) => (
											<div className="flex w-full space-x-1" key={index}>
												<p className={`text-sm ${themeColor?.text}`}>{path.name}</p>

												<div className="flex space-x-1">
													<p className={`text-sm ${themeColor?.text}`}>{path.in.name}</p>
													<BsArrowRight className={`text-xl ${themeColor?.text}`} />
													<p className={`text-sm ${themeColor?.text}`}>{path.out.name}</p>
												</div>
											</div>
										))}
									</div>
								</div>
							))}

							{/* Receiver Account Type Section */}
							<div className="flex-col w-full items-start justify-start">
								<div className="flex w-full space-x-1 items-center justify-between">
									<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.slippage}</p>

									<input
										className="flex w-[30%] text-sm border rounded-lg px-3 py-3"
										placeholder="0.0"
										type="number"
										value={slippage ? slippage : ""}
										onChange={(e) => {
											setSlippage(e.target.value);
										}}
									/>
								</div>
								<div className="flex w-full space-x-1 items-center justify-between">
									<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.minimumReceived}</p>
									<p className={`text-sm ${themeColor?.text}`}>{minimumReceived}</p>
								</div>
								<div className="flex w-full space-x-1 items-center justify-between">
									<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.priceImpact}</p>
									<p className={`text-sm ${swapPriceImpact > 2 ? "text-candy-red" : themeColor?.text}`}>
										{swapPriceImpact &&
											swapPriceImpact.toLocaleString(undefined, {
												maximumFractionDigits: 4,
											})}
										%
									</p>
								</div>
								<div className="flex w-full space-x-1 items-center justify-between">
									<p className={`text-sm ${themeColor?.text}`}>
										{payingAsset?.["unit-name"] + " " + appLanguage?.price}
									</p>
									<div className="flex items-center space-x-1">
										<img
											className={`w-4 h-4 ${themeColor?.text}`}
											alt="asset"
											src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${buyingAsset?.["asset-id"]}&width=30}`}
										/>
										<p className={`text-sm ${themeColor?.text}`}>
											{buyingAsset?.["unit-name"]}{" "}
											{payingAssetPrice &&
												payingAssetPrice.toLocaleString(undefined, {
													maximumFractionDigits:
														getQuotationType === "input"
															? buyingAsset.decimals
															: payingAsset.decimals,
												})}
										</p>
									</div>
								</div>
								<div className="flex w-full space-x-1 items-center justify-between">
									<p className={`text-sm ${themeColor?.text}`}>
										{buyingAsset?.["unit-name"] + " " + appLanguage?.price}
									</p>
									<div className="flex items-center space-x-1">
										<img
											className={`w-4 h-4 ${themeColor?.text}`}
											alt="asset"
											src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${payingAsset?.["asset-id"]}&width=30`}
										/>
										<p className={`text-sm ${themeColor?.text}`}>
											{payingAsset?.["unit-name"]}{" "}
											{buyingAssetPrice &&
												buyingAssetPrice.toLocaleString(undefined, {
													maximumFractionDigits:
														getQuotationType === "input"
															? payingAsset.decimals
															: buyingAsset.decimals,
												})}
										</p>
									</div>
								</div>
								<div className="flex w-full space-x-1 items-center justify-between">
									<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.yourNetGains}</p>
									<p className={`text-sm ${themeColor?.text}`}>
										{buyingAsset?.["unit-name"] +
											" " +
											(swapQuoteProfit.amount / 1000000).toLocaleString(undefined, {
												maximumFractionDigits: 6,
											})}
									</p>
								</div>
							</div>
						</div>
					)}

					{/* Swap Button Section */}
					{swapQuoteRoutes && (
						<div className="flex w-full items-center justify-center space-x-2">
							{inputSwapQuoteLoading ? (
								<img className="animate-spin mx-auto h-5 w-auto" src="/media/logo.png" alt="img" />
							) : insufficientAmount ? (
								<button
									className={`w-full text-md font-bold px-7 py-3 rounded-2xl ${themeColor?.bg} bg-opacity-40 text-white`}
									disabled
								>
									{(getQuotationType === "input" && payingAsset["asset-id"] === -1) ||
									(getQuotationType === "output" && buyingAsset["asset-id"] === -1) ? (
										<p className="text text-sm font-medium">
											{appLanguage?.availableConvert}
											<br />
											{minimumBalance}
										</p>
									) : (
										appLanguage?.sendAmountTooHigh
									)}
								</button>
							) : (
								<button
									className={`w-full text-md font-bold px-7 py-3 rounded-2xl text-white ${
										swapPriceImpact > 2 ? "bg-candy-red" : themeColor?.bg
									}`}
									onClick={() => GetSwapTransactions()}
								>
									{appLanguage?.proceed}
								</button>
							)}

							<button
								className={`w-fit text-md font-bold px-4 py-2 rounded-2xl text-white ${themeColor?.bg}`}
								onClick={() => ClearAllStatesAndValues()}
							>
								{appLanguage?.clear}
							</button>
						</div>
					)}

					{/* Bottom Footer section buy, clear and buttons */}
					<div className="flex flex-col w-full items-center justify-between">
						<p
							className={`text-sm font-medium text-center ${themeColor?.text}`}
							dangerouslySetInnerHTML={{ __html: appLanguage?.convertServiceProvidedBy }}
						/>
					</div>
				</div>

				<SwapQuotationFailedModal isOpen={swapQuotationFailedModalIsOpen} setIsOpen={setSwapQuotationFailedModalIsOpen} />

				<SendSwapTransactionModal
					isOpen={sendSwapTransactionModalIsOpen}
					setIsOpen={setSendSwapTransactionModalIsOpen}
					payingAsset={payingAsset}
					payingAmount={payingAmount}
					buyingAsset={buyingAsset}
					buyingAmount={buyingAmount}
					minimumReceived={minimumReceived}
					swapQuoteRoutes={swapQuoteRoutes}
					swapQuoteProfit={swapQuoteProfit}
					sendPayment={SendTransaction}
				/>

				<SwapTransactionSuccessModal
					isOpen={transactionSuccessModalIsOpen}
					setIsOpen={setTransactionSuccessModalIsOpen}
					modalInfo={transactionSuccessModalInfo}
					navigatePage={navigatePage}
				/>

				<TransactionFailedModal
					isOpen={transactionFailedModalIsOpen}
					setIsOpen={setTransactionFailedModalIsOpen}
					modalInfo={transactionFailedModalInfo}
				/>
			</div>
		</Transition>
	);
};
export default SwapAssets;
